import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus('idle'), 3000);
      return;
    }

    setSubscribeStatus('loading');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d8ulo5n43hkdi0qge4v0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus('idle'), 3000);
    } catch {
      setSubscribeStatus('error');
      setTimeout(() => setSubscribeStatus('idle'), 3000);
    }
  };

  return (
    <footer className="bg-primary-900 text-background-50 rounded-t-2xl mt-8">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              className="inline-block cursor-pointer"
            >
              <span className="font-heading text-2xl font-bold text-white">
                Hoobuy<span className="text-primary-400">Spreadsheet</span>
              </span>
            </a>
            <p className="text-sm text-primary-200/80 mt-4 leading-relaxed">
              {t('footer.brandDesc')}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white mb-4">
              Links
            </h4>
            <div className="space-y-2.5">
              <a
                href="/"
                onClick={(e) => { e.preventDefault(); navigate('/'); }}
                className="block text-sm text-primary-200 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                Início
              </a>
              <a
                href="/guia-de-compras"
                onClick={(e) => { e.preventDefault(); navigate('/guia-de-compras'); }}
                className="block text-sm text-primary-200 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                Guia de Compras
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block text-sm text-primary-200 hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                Hoobuy Sheet
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white mb-4">
              {t('footer.social')}
            </h4>
            <div className="space-y-2.5">
              <a href="#" className="flex items-center gap-2.5 text-sm text-primary-200 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-instagram-line text-base"></i>
                {t('footer.instagram')}
              </a>
              <a href="#" className="flex items-center gap-2.5 text-sm text-primary-200 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-youtube-line text-base"></i>
                {t('footer.youtube')}
              </a>
              <a href="#" className="flex items-center gap-2.5 text-sm text-primary-200 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-facebook-line text-base"></i>
                {t('footer.facebook')}
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white mb-4">
              {t('footer.newsletter')}
            </h4>
            <p className="text-sm text-primary-200/80 mb-4">
              {t('footer.newsletterDesc')}
            </p>
            <form
              onSubmit={handleSubscribe}
              data-readdy-form="true"
              className="flex flex-col sm:flex-row gap-2"
            >
              <input
                type="text"
                name="website_alt"
                className="anti-spam-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer.emailPlaceholder')}
                className="flex-1 px-4 py-2.5 text-sm bg-primary-800 border border-primary-600 rounded-full text-white placeholder:text-primary-300/70 focus:outline-none focus:border-primary-300 transition-colors"
              />
              <button
                type="submit"
                disabled={subscribeStatus === 'loading'}
                className="px-5 py-2.5 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {subscribeStatus === 'loading' ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : subscribeStatus === 'success' ? (
                  <i className="ri-check-line"></i>
                ) : (
                  t('footer.subscribe')
                )}
              </button>
            </form>
            {subscribeStatus === 'success' && (
              <p className="text-xs text-accent-400 mt-2">Inscrito com sucesso!</p>
            )}
            {subscribeStatus === 'error' && (
              <p className="text-xs text-red-400 mt-2">Erro ao inscrever. Tente novamente.</p>
            )}
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-primary-700/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-primary-200">
            {t('footer.copyright')}
          </p>
          <div className="relative">
            <span className="text-6xl md:text-8xl font-heading font-black text-primary-800/25 select-none">
              HOO BUY
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}